import { parseVisibilityTime, VISIBILITY_TIME_FORMAT } from 'utils/time'
import { normalizeSchedules } from './multiSchedule'

export const DEFAULT_VISIBILITY = 'all_day'
export const DEFAULT_VISIBILITY_RANGES = [{ start: null, end: null }]

/**
 * GOLD-12204: Per-schedule visibility applies when the study multi_schedule flag is on.
 * Legacy metadata-level placement is used when the flag is off.
 */
export const usesPerScheduleVisibility = (hasMultiSchedule, _schedules, _instrumentReuse = false) =>
  Boolean(hasMultiSchedule)

export const resolveScheduleVisibility = (schedule, metadataFallback = {}) => {
  const visibility =
    schedule?.visibility ?? metadataFallback?.visibility ?? DEFAULT_VISIBILITY
  const visibilityRanges =
    schedule?.visibilityRanges ??
    metadataFallback?.visibilityRanges ??
    DEFAULT_VISIBILITY_RANGES

  return {
    visibility,
    visibilityRanges: Array.isArray(visibilityRanges)
      ? visibilityRanges.map(range => ({ ...range }))
      : DEFAULT_VISIBILITY_RANGES.map(range => ({ ...range })),
  }
}

export const ensureScheduleVisibilityDefaults = (schedule, metadataFallback = {}) => {
  const resolved = resolveScheduleVisibility(schedule, metadataFallback)
  return {
    ...schedule,
    visibility: resolved.visibility,
    visibilityRanges: resolved.visibilityRanges,
  }
}

/**
 * Copy metadata-level visibility onto each schedule when loading multi-schedule instruments.
 */
export const hydrateSchedulesWithVisibility = (schedules, metadata, hasMultiSchedule) => {
  const list = normalizeSchedules(schedules)
  if (!usesPerScheduleVisibility(hasMultiSchedule, list, metadata?.instrument_reuse)) {
    return list
  }
  return list.map(schedule => ensureScheduleVisibilityDefaults(schedule, metadata))
}

export const stripScheduleVisibility = schedule => {
  if (!schedule || typeof schedule !== 'object') return schedule
  const next = { ...schedule }
  delete next.visibility
  delete next.visibilityRanges
  return next
}

export const stripMetadataVisibility = metadata => {
  if (!metadata || typeof metadata !== 'object') return metadata
  const next = { ...metadata }
  delete next.visibility
  delete next.visibilityRanges
  return next
}

export const formatVisibilityRanges = (visibility, visibilityRanges) => {
  if (visibility !== 'specific_time' || !Array.isArray(visibilityRanges)) return visibilityRanges

  return visibilityRanges.map(range => {
    const { start, end } = range || {}
    const startMoment = parseVisibilityTime(start)
    const endMoment = parseVisibilityTime(end)
    return {
      start: startMoment ? startMoment.format(VISIBILITY_TIME_FORMAT) : null,
      end: endMoment ? endMoment.format(VISIBILITY_TIME_FORMAT) : null,
    }
  })
}

const withFormattedVisibility = (target, visibility, visibilityRanges) => {
  const formattedRanges = formatVisibilityRanges(visibility, visibilityRanges)
  const next = { ...target, visibility }
  if (visibility === 'specific_time') {
    next.visibilityRanges = formattedRanges
  } else {
    delete next.visibilityRanges
  }
  return next
}

/**
 * Attach visibility to each schedule for multi-schedule save payloads and strip metadata-level fields.
 */
export const applyPerScheduleVisibilityForSave = ({
  schedules,
  metadata,
  currentSchedule,
  currentScheduleId,
}) => {
  const metadataVisibility = resolveScheduleVisibility(null, metadata)
  const list = normalizeSchedules(schedules)

  const nextSchedules = list.map(schedule => {
    const scheduleId = schedule?.schedule_id || schedule?.scheduleId
    const isActiveSchedule =
      currentSchedule &&
      currentScheduleId &&
      scheduleId &&
      String(scheduleId) === String(currentScheduleId)

    const source = isActiveSchedule ? currentSchedule : schedule
    const resolved = resolveScheduleVisibility(source, metadata)
    return withFormattedVisibility(
      stripScheduleVisibility(schedule),
      resolved.visibility,
      resolved.visibilityRanges,
    )
  })

  return {
    schedules: nextSchedules,
    metadata: stripMetadataVisibility(metadata),
  }
}

/**
 * Keep metadata-level visibility for legacy / single-schedule payloads and strip schedule-level fields.
 */
export const applyMetadataVisibilityForSave = ({ schedules, metadata, currentSchedule }) => {
  const source = currentSchedule || normalizeSchedules(schedules)[0]
  const resolved = resolveScheduleVisibility(source, metadata)
  const formattedMetadata = withFormattedVisibility(
    { ...metadata },
    resolved.visibility,
    resolved.visibilityRanges,
  )

  const nextSchedules = normalizeSchedules(schedules).map(schedule => stripScheduleVisibility(schedule))

  return {
    schedules: nextSchedules,
    metadata: formattedMetadata,
  }
}
